import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

/**
 * Custom Hook for Baby Name Modal Logic
 * Shows modal when:
 * - User has baby (has_baby = true)
 * - But baby name is null/empty
 * - User registered 24+ hours ago
 * - Modal hasn't been shown in last 24 hours
 */
const useBabyNameModal = () => {
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkModalVisibility = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) { setLoading(false); return; }
        // first ensure session exists
        const me = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        if (!me.ok) { setLoading(false); return; }

        const response = await fetch(`${API_BASE_URL}/api/profile`, {
          credentials: 'include',
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        if (!response.ok) { setLoading(false); return; }

        const profile = await response.json();
        setUserProfile(profile);

        // Check if modal should be shown
        const shouldShow = shouldShowBabyNameModal(profile);

        if (shouldShow) {
          setShouldShowModal(true);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error checking baby name modal visibility:', error);
        setLoading(false);
      }
    };

    checkModalVisibility();
  }, []);

  const shouldShowBabyNameModal = (profile) => {
    if (!profile) {
      return false;
    }

    // Check if user has baby but no name
    const hasBabyButNoName = profile.hasBaby === true && (!profile.babyName || profile.babyName.trim() === '');

    if (!hasBabyButNoName) {
      return false;
    }

    // Check if user registered 24+ hours ago
    const userCreatedAt = profile.user?.createdAt || profile.createdAt;

    if (!userCreatedAt) {
      return false;
    }

    const now = new Date();
    const createdAt = new Date(userCreatedAt);
    const hoursSinceRegistration = (now - createdAt) / (1000 * 60 * 60);

    if (hoursSinceRegistration < 24) {
      return false;
    }

    // Check if modal was shown in last 24 hours
    if (profile.lastModalShown) {
      const lastShown = new Date(profile.lastModalShown);
      const hoursSinceLastShown = (now - lastShown) / (1000 * 60 * 60);

      if (hoursSinceLastShown < 24) {
        return false;
      }
    }

    return true;
  };

  const hideModal = () => {
    setShouldShowModal(false);
  };

  const updateModalShownTime = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/api/profile/update-modal-shown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error updating modal shown time:', error);
    }
  };

  const saveBabyName = async (name) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/profile/update-baby-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ babyName: name })
      });

      if (response.ok) {
        setUserProfile(prev => ({ ...prev, babyName: name }));
        await updateModalShownTime();
        hideModal();
        // Dispatch global event so components like TopBar can refresh
        window.dispatchEvent(new CustomEvent('profileUpdated'));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving baby name:', error);
      return false;
    }
  };

  const skipForNow = async () => {
    await updateModalShownTime();
    hideModal();
  };

  return {
    shouldShowModal: shouldShowModal && !loading,
    userProfile,
    loading,
    saveBabyName,
    skipForNow,
    hideModal
  };
};

export default useBabyNameModal;